import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { reorderCategories } from 'redux/actions/Menu/categories';

const SortableItem = ({ category }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? '#e3f2fd' : '#f5f5f5',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        '&:hover': {
          backgroundColor: '#eeeeee',
        },
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <ListItemIcon>
        <DragIndicator sx={{ color: '#9E9EAD' }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {category.name}
          </Typography>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            {category.productsCount || 0} items
          </Typography>
        }
      />
    </ListItem>
  );
};

const CategorySortModal = ({ open, onClose, categories }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sortedCategories, setSortedCategories] = useState(categories || []);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update sorted categories when categories prop changes
  React.useEffect(() => {
    if (categories) {
      setSortedCategories([...categories]);
      setHasChanges(false);
    }
  }, [categories, open]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedCategories((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  }, []);

  const handleSave = async () => {
    if (hasChanges) {
      const categoryIds = sortedCategories.map((category) => category._id);
      try {
        await dispatch(reorderCategories(categoryIds));
        setHasChanges(false);
        onClose();
      } catch (error) {
        console.error('Failed to save category order:', error);
      }
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    setSortedCategories([...categories]);
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div">
          Sort Categories
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drag and drop categories to reorder them. Changes will be saved when
          you click &quot;Save&quot;.
        </Typography>

        {sortedCategories.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sortedCategories.map((cat) => cat._id)}
              strategy={verticalListSortingStrategy}
            >
              <List sx={{ padding: 0, overflowX: 'hidden' }}>
                {sortedCategories.map((category) => (
                  <SortableItem key={category._id} category={category} />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No categories to sort
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges}
          sx={{
            backgroundColor: hasChanges ? '#1976d2' : undefined,
          }}
        >
          {'SAVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategorySortModal;
